/**
 * Reporting hook for TAS relationship links that read null in the store despite NOT NULL
 * foreign keys behind them — the graph transiently loses application→instance or
 * instance→template, and a guarded getter degrading silently would erase the only signal
 * that it happened. tio-data cannot depend on tio-common (dependency direction), so the
 * sink is registered by the app at boot; until then reports fall back to console.error.
 */

interface SeveredLinkContext {
  /** The record type whose getter observed the null, e.g. 'tas-course'. */
  observer: string;
  /** The edge that read null, e.g. 'tasApplication.tasProgramInstance'. */
  edge: string;
  /** Id of the record whose getter observed the null, when available. */
  recordId?: string;
}

type SeveredLinkSink = (error: Error, context: SeveredLinkContext) => void;

let sink: SeveredLinkSink | null = null;
const reported = new Set<string>();

/**
 * Install the app's reporting sink (e.g. Datadog addError + first-party trackError).
 * Replaces any previous sink; call once from the application route.
 */
export function setSeveredLinkSink(nextSink: SeveredLinkSink): void {
  sink = nextSink;
}

/**
 * Report a severed link once per (observer, edge, recordId) tuple per page load, so a
 * getter re-evaluating during render cannot flood the sink. Never throws: reporting is
 * diagnostics for a degraded state, and it must not replace one crash with another.
 */
export function reportSeveredLink(context: SeveredLinkContext): void {
  const key = `${context.observer}:${context.edge}:${context.recordId ?? ''}`;
  if (reported.has(key)) {
    return;
  }
  reported.add(key);

  const error = new Error(
    `TAS severed link: ${context.observer} read null crossing ${context.edge} — ` +
      'client-side relationship state, not missing data (the FKs are NOT NULL)'
  );
  try {
    if (sink) {
      sink(error, context);
    } else {
      console.error(error.message, context);
    }
  } catch {
    // A broken sink must not take the guarded getter down with it.
  }
}

/** Test hook: clears the once-per-page dedupe set and the installed sink. */
export function resetSeveredLinkReportingForTests(): void {
  sink = null;
  reported.clear();
}
